import {
	type FetchUserTransactionHistorySummaryByTypeParams,
	type FetchUserTransactionHistorySummaryLastByTypeParams,
	type PaginationWithDetailedPaginationMode,
	secondsToMs
} from '@capsicum/common';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchUserTransactionHistorySummaryByType, fetchUserTransactionHistorySummaryLastByType } from '../api';

const queryKeyForFetchInfiniteUserTransactionHistorySummaryByType = 'fetchInfiniteUserTransactionHistorySummaryByType';

export const useUserTransactionHistoryQueryCacheInvalidation = () => {
	const queryClient = useQueryClient();

	return () => {
		queryClient.invalidateQueries({ queryKey: [queryKeyForFetchInfiniteUserTransactionHistorySummaryByType] });
	};
};

export type UseInfiniteUserTransactionHistoryQueryParams = FetchUserTransactionHistorySummaryByTypeParams &
	Required<Pick<FetchUserTransactionHistorySummaryByTypeParams, 'limit'>>;

/**
 * @returns user transaction history summary by type
 */
export const useInfiniteUserTransactionHistoryQuery = (params: UseInfiniteUserTransactionHistoryQueryParams) =>
	useInfiniteQuery({
		getNextPageParam: lastPage => {
			// We should be using cursors here
			// const nextOffset = lastPage.metadata?.cursor;
			const metadata = lastPage.metadata as PaginationWithDetailedPaginationMode['metadata'];
			const currentPageCursor = Number(metadata?.current_page) || 1;
			const lastPageCursor = Number(metadata?.last_page) || 1;

			if (currentPageCursor < lastPageCursor) {
				return currentPageCursor + 1;
			}

			return undefined;
		},
		initialPageParam: 1,
		queryFn: async ({ pageParam }: { pageParam: number }) => {
			let result;
			let page = pageParam;
			let reachedLastPage = false;

			do {
				result = await fetchUserTransactionHistorySummaryByType({
					...params,
					page
				});

				const metadata = result.metadata as PaginationWithDetailedPaginationMode['metadata'];

				reachedLastPage = page >= Number(metadata?.last_page);
				++page;
			} while (result.data.length === 0 && !reachedLastPage);

			return result;
		},
		queryKey: [queryKeyForFetchInfiniteUserTransactionHistorySummaryByType, params],
		select: data => data.pages.flatMap(page => page.data),
		staleTime: secondsToMs(10)
	});

export const useUserTransactionHistoryLastQuery = (params: FetchUserTransactionHistorySummaryLastByTypeParams) =>
	useQuery({
		queryFn: () => fetchUserTransactionHistorySummaryLastByType(params),
		queryKey: ['fetchUserTransactionHistorySummaryLastByType', params]
	});
